import { Input } from '@components/ui/input'
import { InputGroup, InputGroupPrefix, InputGroupSuffix } from '@components/ui/input-group'
import { Tooltip, TooltipContent, TooltipTrigger } from '@components/ui/tooltip'
import { cn } from '@/utils'
import { handleOffsetEnterKey } from '../handlers/offset-enter-key'
import type { OffsetSide } from '../types/wrap-offset'

interface SideOffsetInputProps {
  side: OffsetSide
  disabled: boolean
  value: string
  mixed: boolean
  errorMessage: string
  onChange: (raw: string) => void
  onFocus: () => void
  onBlur: (raw: string) => void
}

/** One directional offset input: arrow icon with tooltip, value, "pt" suffix and inline error. */
export function SideOffsetInput({
  side: { key, Icon, label, tooltip },
  disabled,
  value,
  mixed,
  errorMessage,
  onChange,
  onFocus,
  onBlur,
}: SideOffsetInputProps) {
  return (
    <div className="flex flex-col gap-1">
      <InputGroup className="w-full">
        <InputGroupPrefix>
          <Tooltip>
            <TooltipTrigger asChild>
              {/* InputGroupPrefix is `pointer-events-none` (so it doesn't block clicks
                  into the input behind it) — re-enable pointer events on just this
                  trigger so hover actually reaches it. */}
              <span className="inline-flex pointer-events-auto">
                <Icon className="h-3.5 w-3.5 text-muted-foreground" aria-hidden />
              </span>
            </TooltipTrigger>
            <TooltipContent side="top">{tooltip}</TooltipContent>
          </Tooltip>
        </InputGroupPrefix>
        <Input
          type="text"
          inputMode="decimal"
          aria-label={label}
          disabled={disabled}
          value={value}
          placeholder={mixed ? 'Mixed' : undefined}
          onChange={(e) => onChange(e.target.value)}
          onFocus={onFocus}
          onBlur={(e) => onBlur(e.target.value)}
          onKeyDown={(e) => handleOffsetEnterKey(e, errorMessage !== '')}
          size="compact"
          error={errorMessage !== ''}
          aria-describedby={errorMessage ? `${key}-offset-error` : undefined}
          className={cn(
            'h-9 w-full sm:w-full border-border/80 pl-7 pr-7 text-sm',
            value !== '' && 'text-base-foreground',
            mixed && value === '' && 'placeholder:text-base-foreground'
          )}
        />
        <InputGroupSuffix>
          <span className="text-xs font-medium text-muted-foreground leading-none">pt</span>
        </InputGroupSuffix>
      </InputGroup>
      {errorMessage && (
        <span id={`${key}-offset-error`} role="alert" className="text-xs text-destructive">
          {errorMessage}
        </span>
      )}
    </div>
  )
}
